import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { AppButton } from "../../../components/form";
import { useDeploymentMode } from "../../../hooks/useDeploymentMode";
import { HOME_GUIDE_CARDS, type HomeGuideCardId } from "../homeGuideConfig";
import {
  readHomeGuideState,
  setHomeGuideDismissed,
  type HomeGuideState,
} from "../homeGuideStorage";
import { Glyph, IconTile, Section, type Tone } from "./parts";
import HomeGuideWizardDialog from "./HomeGuideWizardDialog";

type Props = {
  userId: string;
  workspaceId: string;
};

export default function HomeGettingStarted({ userId, workspaceId }: Props) {
  const { t } = useTranslation();
  const { vpnEnabled } = useDeploymentMode();
  const [state, setState] = useState<HomeGuideState>(() => readHomeGuideState(userId, workspaceId));
  const [forceShow, setForceShow] = useState(false);
  const [activeCard, setActiveCard] = useState<HomeGuideCardId | null>(null);

  useEffect(() => {
    setState(readHomeGuideState(userId, workspaceId));
    setForceShow(false);
  }, [userId, workspaceId]);

  const showGrid = forceShow || !state.dismissed;
  const completed = useMemo(() => new Set(state.completedCardIds), [state.completedCardIds]);

  const refresh = useCallback(() => {
    setState(readHomeGuideState(userId, workspaceId));
  }, [userId, workspaceId]);

  const dismiss = () => {
    setState(setHomeGuideDismissed(userId, workspaceId, true));
    setForceShow(false);
  };

  const reopen = () => {
    setForceShow(true);
  };

  if (!showGrid) {
    return (
      <Box sx={{ px: 0.5 }}>
        <AppButton variant="text" size="small" startIcon={<Glyph name="rocket_launch" fontSize="small" />} onClick={reopen}>
          {t("home.guide.reopen")}
        </AppButton>
      </Box>
    );
  }

  return (
    <>
      <Section
        index={0}
        title={t("home.guide.title")}
        description={t("home.guide.description")}
        action={
          <AppButton variant="text" size="small" onClick={dismiss}>
            {t("home.guide.dontShowAgain")}
          </AppButton>
        }
      >
        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Grid container spacing={1.5}>
            {HOME_GUIDE_CARDS.map((card) => {
              const done = completed.has(card.id);
              return (
                <Grid key={card.id} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card
                    variant="outlined"
                    sx={(theme) => ({
                      height: "100%",
                      opacity: done ? 0.72 : 1,
                      borderColor: done ? alpha(theme.palette.success.main, 0.45) : undefined,
                    })}
                  >
                    <CardActionArea sx={{ height: "100%" }} onClick={() => setActiveCard(card.id)}>
                      <CardContent>
                        <Stack spacing={1.25}>
                          <Stack direction="row" spacing={1.25} alignItems="flex-start">
                            <IconTile name={card.icon} tone={(card.tone || "primary") as Tone} />
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography variant="subtitle2" fontWeight={600}>
                                  {t(card.titleKey)}
                                </Typography>
                                {done && (
                                  <Chip size="small" color="success" variant="outlined" label={t("home.guide.done")} />
                                )}
                              </Stack>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {t(card.descriptionKey)}
                              </Typography>
                            </Box>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Section>

      <HomeGuideWizardDialog
        open={activeCard != null}
        cardId={activeCard}
        userId={userId}
        workspaceId={workspaceId}
        vpnEnabled={vpnEnabled}
        onClose={() => setActiveCard(null)}
        onCompleted={() => refresh()}
      />
    </>
  );
}
